import { ChapterRecords } from "./ChapterRecords.js";

function objectWithTimestamp(obj) {
	obj.sT = new Date().toISOString();
	return obj;
}

export class Session {
	constructor(id, heartbeat_rate, epub_id) {
		this.id = id;
		this.epub_id = epub_id;
		this.chapterRecords = new ChapterRecords(this.id, this.epub_id);
		this.heartbeat_rate = heartbeat_rate * 1000;
	}

	/** Starts a new session and instantiates its record */
	startSession(trackerEvent) {
		this.chapterRecords.startSession(
			objectWithTimestamp({
				id: this.id,
				tE: trackerEvent,
			}),
		);
		this.heartbeat = true;
		this.trackerState = trackerEvent;
		this.scheduleHeartbeat();
	}

	scheduleHeartbeat() {
		clearTimeout(this.heartbeatTimer);
		// An arrow function, so that `this` is still the session when the
		// timer calls it: passing the bare method stopped the heartbeat
		// after its first tick.
		this.heartbeatTimer = setTimeout(() => {
			this.updateHeartbeat();
		}, this.heartbeat_rate);
	}

	/** Updates the heartbeat of a session and its record in the store */
	updateHeartbeat() {
		if (!this.heartbeat) return;
		try {
			const lastHeartbeat = this.chapterRecords.getHeartbeat();
			if (lastHeartbeat !== null) {
				const startTime = new Date(lastHeartbeat.time).getTime();
				const endTime = new Date().getTime();
				const elapsed = endTime - startTime;
				if (elapsed >= 900 * 1000) {
					this.startSession(this.trackerState);
				} else {
					this.chapterRecords.setHeartbeat(lastHeartbeat);
					this.chapterRecords.updateSession("h", lastHeartbeat.increment);
					this.scheduleHeartbeat();
				}
			}
			/* c8 ignore next 3 */
		} catch (err) {
			console.error(err);
		}
	}

	/** Stops the heartbeat of a session */
	stopHeartbeat() {
		this.heartbeat = false;
		clearTimeout(this.heartbeatTimer);
	}

	/** Retrieves data from user interactions and updates the session’s record */
	click(element, xpath = true) {
		if (
			element.tagName.toLowerCase() === "a" ||
			element.tagName.toLowerCase() === "img"
		) {
			let data = objectWithTimestamp({
				id: element.tagName.toLowerCase() + "#" + element.id,
				t: "cl",
			});

			if (xpath) {
				const nodePath = this.getXPathForElement(element);
				if (nodePath.length > 0) {
					data.xpath = nodePath;
				}
			}

			const nearestAnchor = element.closest("a[href]");
			if (nearestAnchor) {
				data.href = nearestAnchor.href;
			}
			this.chapterRecords.updateSession("I", data, true);
		}
	}

	annotation(type, uuid) {
		const data = objectWithTimestamp({ type, uuid });
		this.chapterRecords.updateSession("aE", data, true);
	}

	progress(type, progression, spread, duration, page, pageCount) {
		const data = objectWithTimestamp({
			t: type,
			p: progression,
			sp: spread,
			d: duration,
		});
		// Page-based readers (the PDF reader) also record the page and the
		// page count; EPUB readers never set them.
		if (Number.isInteger(page)) data.pg = page;
		if (Number.isInteger(pageCount)) data.pc = pageCount;
		this.chapterRecords.updateSession("rE", data, true);
	}

	preference(type, value) {
		const data = objectWithTimestamp({ t: type, v: value });
		this.chapterRecords.updateSession("pE", data, true);
	}

	share(type, nonce, where) {
		const data = objectWithTimestamp({ t: type, n: nonce, w: where });
		this.chapterRecords.updateSession("sE", data, true);
	}

	/** Stops a session and its recording */
	unload(trackerEvent) {
		this.stopHeartbeat();
		if (trackerEvent) {
			this.chapterRecords.endSession(trackerEvent);
		} else {
			this.chapterRecords.endSession();
		}
	}

	getXPathForElement(el, root) {
		let xpath = "";
		let position;
		let tmpItem;

		if (!root) {
			root = document;
		}

		while (el !== root.documentElement && el.parentNode) {
			position = 0;
			tmpItem = el;
			while (tmpItem) {
				if (tmpItem.nodeType === 1 && tmpItem.nodeName === el.nodeName) {
					position += 1;
				}
				tmpItem = tmpItem.previousSibling;
			}

			xpath = el.nodeName.toLowerCase() + "[" + position + "]" + "/" + xpath;

			el = el.parentNode;
		}

		xpath = "/" + xpath;
		xpath = xpath.replace(/\/$/, "");

		return xpath;
	}
}
